import { DialogActions, ErrorMessage } from '@/components/Elements';
import { InputField, ToggleField } from '@/components/Form';
import { useArticleSearchCondition } from '@/features/analyze/hooks/useArticleSearchCondition';
import { graphql, readFragment, useMutation, useQuery } from '@/lib/graphql';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCaution } from './AlertCaution';
import { FilterSelectionList } from './FilterSelectionList';
import { SavedFilterItem } from './SavedFilterItem';

// 保存されたフィルター一覧を取得するQuery
const getSavedFiltersForOverwriteModeQuery = graphql(
  `
    query getSavedFiltersForOverwriteMode {
      savedFilters {
        id
        ...FilterSelectionFragment
      }
    }
  `,
  [FilterSelectionList.fragments.data]
);

// フィルター上書き用のMutation
const updateSavedFilterForOverwriteMutation = graphql(
  `
    mutation updateSavedFilter($input: UpdateSavedFilterInput!) {
      updateSavedFilter(input: $input) {
        savedFilter {
          ...SavedFilterItemFragment
        }
        affectedSavedFilters {
          ...SavedFilterItemFragment
        }
        userErrors {
          message
          code
        }
      }
    }
  `,
  [SavedFilterItem.fragments.data]
);

interface OverwriteFilterModeProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const OverwriteFilterMode = ({
  onClose,
  onSuccess,
}: OverwriteFilterModeProps) => {
  const { t } = useTranslation();
  const [selectedFilterId, setSelectedFilterId] = useState<string | null>(null);
  const [filterName, setFilterName] = useState('');
  const [alertEnabled, setAlertEnabled] = useState(false);

  const condition = useArticleSearchCondition();

  // 保存されたフィルター一覧を取得
  const { data: savedFiltersData, loading: isLoadingFilters } = useQuery(
    getSavedFiltersForOverwriteModeQuery
  );

  const savedFilters = savedFiltersData?.savedFilters || [];

  const [updateSavedFilter, { data, loading }] = useMutation(
    updateSavedFilterForOverwriteMutation,
    {
      update: (cache, { data }) => {
        data?.updateSavedFilter?.affectedSavedFilters?.forEach(
          (affectedFilterRef) => {
            const affectedFilter = readFragment(
              SavedFilterItem.fragments.data,
              affectedFilterRef
            );
            if (!affectedFilter) return;

            cache.writeFragment({
              id: cache.identify({
                __typename: 'SavedFilter',
                id: affectedFilter.id,
              }),
              fragment: SavedFilterItem.fragments.data,
              data: affectedFilter,
            });
          }
        );
      },
    }
  );

  const handleFilterSelect = (filterId: string) => {
    setSelectedFilterId(filterId);
    const filter = savedFilters.find((f) => f.id === filterId);
    if (filter) {
      const savedFilter = readFragment(
        FilterSelectionList.fragments.data,
        filter
      );
      setFilterName(savedFilter.name);
      setAlertEnabled(savedFilter.alertEnabled);
    }
  };

  const handleOverwrite = async () => {
    if (!selectedFilterId || !filterName.trim()) return;

    const result = await updateSavedFilter({
      variables: {
        input: {
          id: selectedFilterId,
          name: filterName.trim(),
          alertEnabled,
          condition,
        },
      },
    });

    if (result.data?.updateSavedFilter?.savedFilter) {
      onSuccess();
    }
  };

  const canOverwrite = selectedFilterId && filterName.trim() !== '';

  return (
    <div className="space-y-6">
      {/* フィルター選択 */}
      {!isLoadingFilters && (
        <FilterSelectionList
          filters={savedFilters}
          selectedFilterId={selectedFilterId}
          onSelectFilter={handleFilterSelect}
        />
      )}

      {selectedFilterId && (
        <div className="space-y-4">
          <InputField
            label={t('フィルタ名')}
            value={filterName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilterName(e.target.value)
            }
            placeholder={t('フィルタ名を入力してください')}
            className="border-rw-sub"
          />

          <div className="space-y-2">
            <ToggleField
              label={t('通知を受け取る')}
              checked={alertEnabled}
              onToggle={(checked) => setAlertEnabled(checked)}
              size="sm"
            />
            <div
              className={clsx(
                'overflow-hidden transition-all duration-500 ease-in-out',
                alertEnabled ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
              )}
            >
              <AlertCaution />
            </div>
          </div>
        </div>
      )}

      {/* エラーメッセージ */}
      <ErrorMessage errors={data?.updateSavedFilter?.userErrors} />

      {/* アクションボタン（下部） */}
      <DialogActions
        onCancel={onClose}
        onConfirm={handleOverwrite}
        confirmText={t('上書き保存')}
        isConfirmDisabled={!canOverwrite}
        isConfirmLoading={loading}
      />
    </div>
  );
};
